import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { prettifyApiError } from "@/shared/api";
import { ErrorBanner } from "@/shared/ui/ErrorBanner";
import { type AuthUser, changeUserPassword } from "../auth/api";
import { useResource } from "./api";

/** Minimo allineato al backend (``_validate_password``). */
const MIN_PASSWORD = 12;

const BTN =
  "rounded-md border border-accent bg-accent-2 px-3.5 py-2 text-[13px] font-medium text-white " +
  "hover:brightness-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-soft " +
  "disabled:opacity-60";

const FIELD =
  "mt-1 h-11 w-full rounded-md border border-line bg-paper px-3 text-[13px] text-ink " +
  "focus-visible:border-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-soft";

function problema(nuova: string, conferma: string): string | null {
  if (nuova.length < MIN_PASSWORD) {
    return `La password deve avere almeno ${MIN_PASSWORD} caratteri.`;
  }
  if (![...nuova].some((c) => !/[a-zA-Z0-9]/.test(c))) {
    return "Serve almeno un carattere speciale (per esempio - . ! ? #).";
  }
  if (nuova !== conferma) return "Le due password non coincidono.";
  return null;
}

/**
 * Card "La tua password" (Impostazioni → Account, web-mode).
 *
 * 🔑 Esiste per TUTTI, non solo per `god`: la gestione utenti è riservata
 * all'amministratore, ma la propria password deve poterla cambiare chiunque —
 * altrimenti la scadenza annuale diventa un modo per chiudere fuori le
 * persone invece che una misura di sicurezza. Il backend lo consente già
 * (``change_password`` accetta l'utente su sé stesso, con re-auth).
 */
export function PasswordCard() {
  const [params] = useSearchParams();
  const me = useResource<{ user: AuthUser } | null>("/api/auth/me", null);
  const [attuale, setAttuale] = useState("");
  const [nuova, setNuova] = useState("");
  const [conferma, setConferma] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fatto, setFatto] = useState(false);

  // Il login rimanda qui con questo parametro quando la password è scaduta:
  // senza dirlo, chi atterra su Impostazioni non capisce perché ci è finito.
  const scaduta = params.get("password_scaduta") === "1";
  const utente = me.value?.user;

  async function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!utente) return;
    const issue = problema(nuova, conferma);
    if (issue) {
      setError(issue);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await changeUserPassword(utente.id, attuale, nuova);
      setFatto(true);
      setAttuale("");
      setNuova("");
      setConferma("");
      void me.refresh();
    } catch (err) {
      setError(prettifyApiError(err));
    } finally {
      setBusy(false);
    }
  }

  const scadenza = utente?.password_scade_il;

  return (
    <section className="px-5 py-4 max-md:px-4">
      <h3 className="text-[15px] font-bold text-ink max-md:text-[17px]">La tua password</h3>

      {scaduta ? (
        <p
          role="status"
          className="mt-3 rounded-full border border-orange-300 bg-orange-50 px-3.5 py-3 text-[13px] leading-relaxed text-orange-900"
        >
          <strong className="font-medium">La tua password è scaduta.</strong> Puoi continuare a
          lavorare, ma cambiala adesso: la rotazione annuale è parte degli impegni presi con Amazon
          per l'accesso ai dati degli ordini.
        </p>
      ) : (
        <p className="mt-1 text-[12.5px] leading-relaxed text-ink-2">
          Almeno {MIN_PASSWORD} caratteri con un carattere speciale. Va cambiata una volta l'anno.
          {scadenza && (
            <>
              {" "}
              Scade il{" "}
              <span className="font-medium text-ink-2">
                {new Intl.DateTimeFormat("it-IT", { dateStyle: "long" }).format(new Date(scadenza))}
              </span>
              .
            </>
          )}
        </p>
      )}

      <ErrorBanner message={error} className="mt-4 block" />

      {fatto && (
        <p
          role="status"
          className="mt-4 rounded-full border border-line bg-paper px-3.5 py-3 text-[13px] text-ink-2"
        >
          Password cambiata. Le altre sessioni aperte sono state chiuse.
        </p>
      )}

      <form onSubmit={(event) => void submit(event)} className="mt-4 max-w-[420px]">
        <label htmlFor="pwd-attuale" className="block text-[12.5px] font-medium text-ink-2">
          Password attuale
        </label>
        <input
          id="pwd-attuale"
          type="password"
          value={attuale}
          onChange={(event) => setAttuale(event.target.value)}
          autoComplete="current-password"
          required
          disabled={busy}
          className={FIELD}
        />

        <label htmlFor="pwd-nuova" className="mt-3 block text-[12.5px] font-medium text-ink-2">
          Nuova password
        </label>
        <input
          id="pwd-nuova"
          type="password"
          value={nuova}
          onChange={(event) => setNuova(event.target.value)}
          autoComplete="new-password"
          required
          disabled={busy}
          className={FIELD}
        />

        <label htmlFor="pwd-conferma" className="mt-3 block text-[12.5px] font-medium text-ink-2">
          Ripeti la nuova password
        </label>
        <input
          id="pwd-conferma"
          type="password"
          value={conferma}
          onChange={(event) => setConferma(event.target.value)}
          autoComplete="new-password"
          required
          disabled={busy}
          className={FIELD}
        />

        <button type="submit" disabled={busy || !utente} className={`mt-4 ${BTN}`}>
          {busy ? "Cambio in corso…" : "Cambia password"}
        </button>
      </form>
    </section>
  );
}
